import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/parent/productions')({
  component: ParentProductions,
});

interface Appearance {
  cast_id: string;
  dance_id: string;
  dance_name: string;
  production_id: string;
  production_name: string;
  dancer_id: string;
  dancer_name: string;
  shows: {
    show_id: string;
    show_name: string;
    performance_at: string;
    computed_start_time: string | null;
  }[];
}

function useFamilyAppearances() {
  return useQuery({
    queryKey: ['parent-appearances'],
    queryFn: async (): Promise<Appearance[]> => {
      // RLS filters cast_assignments to this parent's dancers; we join in
      // dance + production names + lineup positions for the upcoming shows.
      const { data, error } = await supabase.from('cast_assignments').select(
        `id,
           dance_id,
           dancer_id,
           dancers(id, first_name, last_name, preferred_name),
           dances(id, name, length_seconds,
                  productions(id, name, season))`,
      );
      if (error) throw error;

      // For each unique dance_id, fetch lineup rows + show data.
      const danceIds = [...new Set((data ?? []).map((r) => (r as { dance_id: string }).dance_id))];
      if (danceIds.length === 0) return [];

      const { data: lineupRows, error: e2 } = await supabase
        .from('show_lineups')
        .select(
          `show_id, dance_id, sequence_index, computed_start_time,
           shows(id, name, performance_at)`,
        )
        .in('dance_id', danceIds);
      if (e2) throw e2;

      const lineupByDance = new Map<string, Appearance['shows']>();
      for (const r of (lineupRows ?? []) as {
        show_id: string;
        dance_id: string;
        computed_start_time: string | null;
        shows: { id: string; name: string; performance_at: string } | null;
      }[]) {
        if (!r.shows) continue;
        const list = lineupByDance.get(r.dance_id) ?? [];
        list.push({
          show_id: r.show_id,
          show_name: r.shows.name,
          performance_at: r.shows.performance_at,
          computed_start_time: r.computed_start_time,
        });
        lineupByDance.set(r.dance_id, list);
      }

      return (
        (data ?? []) as {
          id: string;
          dance_id: string;
          dancer_id: string;
          dancers: { first_name: string; last_name: string; preferred_name: string | null } | null;
          dances: {
            id: string;
            name: string;
            productions: { id: string; name: string; season: string } | null;
          } | null;
        }[]
      )
        .filter((r) => r.dances?.productions)
        .map((r) => ({
          cast_id: r.id,
          dance_id: r.dance_id,
          dance_name: r.dances!.name,
          production_id: r.dances!.productions!.id,
          production_name: r.dances!.productions!.name,
          dancer_id: r.dancer_id,
          dancer_name: `${r.dancers?.preferred_name ?? r.dancers?.first_name ?? ''} ${
            r.dancers?.last_name ?? ''
          }`.trim(),
          shows: lineupByDance.get(r.dance_id) ?? [],
        }));
    },
  });
}

function ParentProductions() {
  const appearances = useFamilyAppearances();

  // Group by production for rendering.
  const byProduction = new Map<string, Appearance[]>();
  for (const a of appearances.data ?? []) {
    const list = byProduction.get(a.production_id) ?? [];
    list.push(a);
    byProduction.set(a.production_id, list);
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Recital + competition
        </p>
        <h1 className="mt-2">Productions</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Every dance your dancer is in, when it's scheduled, and the running time.
        </p>
      </header>

      {appearances.isLoading && <p aria-live="polite">Loading…</p>}

      {appearances.data?.length === 0 && (
        <Empty
          title="No appearances yet"
          description="Once the studio casts your dancer in a recital piece, it'll appear here."
        />
      )}

      {byProduction.size > 0 && (
        <div className="space-y-8">
          {[...byProduction.entries()].map(([productionId, items]) => (
            <section key={productionId} aria-labelledby={`p-${productionId}`}>
              <h2
                id={`p-${productionId}`}
                className="font-[var(--font-display)] text-[var(--text-2xl)]"
              >
                {items[0]?.production_name}
              </h2>
              <ul className="mt-4 grid gap-3">
                {items.map((a) => (
                  <li key={a.cast_id} className="surface-elevated p-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-medium text-[var(--text-lg)]">{a.dance_name}</h3>
                      <Badge tone="accent">{a.dancer_name}</Badge>
                    </div>
                    {a.shows.length === 0 ? (
                      <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        Not yet placed in a running order.
                      </p>
                    ) : (
                      <ul className="mt-3 grid gap-1 text-[var(--text-sm)]">
                        {a.shows.map((s) => (
                          <li key={s.show_id} className="text-[var(--color-ink-muted)]">
                            <span className="font-medium text-[var(--color-ink)]">
                              {s.show_name}
                            </span>
                            {' · '}
                            {new Date(s.performance_at).toLocaleString()}
                            {s.computed_start_time && (
                              <>
                                {' · stage time '}
                                {new Date(s.computed_start_time).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </>
                            )}
                            {' · '}
                            <Link
                              to="/parent/shows/$showId/seats"
                              params={{ showId: s.show_id }}
                              className="underline"
                            >
                              Buy tickets
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
